import React, { useState, MouseEvent } from 'react';
import { FieldProps, useFieldHelper } from 'framework/ui/field';
import { InputLoader } from 'framework/ui/mui-builder';
import { Button } from 'framework/ui/mui-builder';
import { useAuthorization } from 'framework/authorization';
import DocumentPreviewModal from '../../../documentStorage/shared/preview/DocumentPreviewModal';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { SxProps } from '@mui/material';
import {
  DocumentMetadataType,
  DocumentStorageContextData,
  FileName,
  useDocumentStorageContext,
} from 'framework/documentStorage';
import useArtifactDownloadHelper from '../../artifactDownloadHelper';

type DownloadFileLinkFieldProps = {
  metadataSource?: string;
  metadataValue?: any;
  pathSource?: string;
  pathVaue?: string;
  showNameSource?: string;
  showNameValue?: string;
  sxButton?: SxProps;
  onPreviewClose?: () => void;
} & FieldProps;

const DownloadFileLinkField: React.FC<DownloadFileLinkFieldProps> = ({
  permission,
  metadataSource = 'metadata',
  pathSource = 'path',
  metadataValue,
  pathValue,
  showNameSource,
  showNameValue,
  sxButton,
  onPreviewClose,
}) => {
  const [file, setFile] = useState<Nullable<string>>(null);
  const [loading, setLoading] = useState(false);
  const { getDocumentToken, renderPreviewDocumentTemplate } = useDocumentStorageContext({
    ignoreContext: true,
  }) as Partial<DocumentStorageContextData>;
  const { isGranted } = useAuthorization();
  const { addMessage } = useMessageService();
  const { downloadDocumentStorage } = useArtifactDownloadHelper();
  const { getValue: getMetadata } = useFieldHelper<DocumentMetadataType>({
    value: metadataValue,
    source: metadataSource,
  });
  const { getValue: getPathFile } = useFieldHelper<string>({ value: pathValue, source: pathSource });
  const { getValue: getShowName } = useFieldHelper<string>({ value: showNameValue, source: showNameSource });

  const metadata = getMetadata();
  const showName = getShowName();
  const filePath = getPathFile();

  const fileName = metadata?.fileName || filePath?.split('/')?.pop() || 'Document';
  const hasAccess = !permission || isGranted(permission);

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    e.currentTarget.blur();
    setLoading(true);
    try {
      if (getDocumentToken) {
        let finalPath = filePath;

        if (renderPreviewDocumentTemplate) {
          const renderedDocument = await renderPreviewDocumentTemplate({ templatePath: filePath });
          if (renderedDocument?.path) {
            finalPath = renderedDocument.path;
          }
        }

        setFile(finalPath);
      } else {
        await downloadDocumentStorage(filePath, fileName);
      }
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    } finally {
      setLoading(false);
    }
  };

  if (!hasAccess) {
    return <FileName title={showName || fileName} />;
  }

  return (
    <>
      <Button
        sx={sxButton || { textAlign: 'initial', maxWidth: '100%', flex: '1 1 auto', justifyContent: 'flex-start' }}
        onClick={handleClick}
        link
      >
        <FileName title={showName || fileName} />
        {loading && <InputLoader className="files-list__files-loader" />}
      </Button>

      {getDocumentToken && file && (
        <DocumentPreviewModal
          previewPath={file}
          filePath={filePath}
          fileName={metadata?.fileName}
          onClose={() => {
            setFile(null);
            onPreviewClose?.();
          }}
        />
      )}
    </>
  );
};

export default DownloadFileLinkField;
